import { differenceInCalendarDays } from "date-fns";
import { CalendarDaysIcon, ExternalLink } from "lucide-react";

import { useMatterLocale } from "@/components/contexts/MatterJurisdictionContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/NewButton";
import { HoverPopover } from "@/components/ui/hover-popover";
import type { Document } from "@/hooks/useDocuments";
import { cn } from "@/lib/utils";
import { formatRegionalDate } from "@/utils/date-utils";

type CorrespondenceDeadline = Document["deadlines"][number];

export type DeadlineTaskStates = Record<string, string | undefined>;

type DeadlinePopoverProps = {
  deadlines: CorrespondenceDeadline[];
  deadlineTaskStates: DeadlineTaskStates;
  slug: string | undefined;
  documentGuid: string;
};

const DeadlineDate = ({ date }: { date: string | Date | null | undefined }) => {
  const locale = useMatterLocale();
  return (
    <div className="inline-flex whitespace-nowrap text-sm font-semibold text-gray-900">
      {formatRegionalDate(date, locale)}
    </div>
  );
};

export const DeadlinePopover = ({
  deadlines,
  deadlineTaskStates,
  slug,
  documentGuid,
}: DeadlinePopoverProps) => {
  const validDeadlines = deadlines.filter((deadline) =>
    deadline.description !== undefined && !Number.isNaN(new Date(deadline.date).getTime())
  );
  const today = new Date();

  if (validDeadlines.length === 0) return null;

  return (
    <HoverPopover
      triggerClassName="flex h-8 w-6 cursor-pointer items-center justify-center text-gray-700"
      trigger={<CalendarDaysIcon size={18} />}
      contentClassName="w-[28rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-gray-200 bg-white p-0 text-gray-900 shadow-xl"
    >
      <>
          <div className="flex items-center justify-between gap-4 border-b border-gray-100 bg-gray-50 px-4 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-700">
                <CalendarDaysIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Deadlines</div>
              </div>
            </div>
            <Badge variant="outline" className="shrink-0 border-gray-200 bg-white text-gray-600">
              {validDeadlines.length}
            </Badge>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {validDeadlines.map((deadline) => {
              const deadlineDate = new Date(deadline.date);
              const daysFromToday = differenceInCalendarDays(deadlineDate, today);
              const deadlineState = deadline.deadline_id
                ? deadlineTaskStates[deadline.deadline_id] ?? deadline.state
                : deadline.state;
              const isCompleted = deadlineState === "Completed";
              const isPastDeadline = !isCompleted && daysFromToday < 0;
              const isDueToday = !isCompleted && daysFromToday === 0;
              const statusLabel = isCompleted
                ? "Completed"
                : isPastDeadline
                  ? "Overdue"
                  : isDueToday
                    ? "Due today"
                    : "Upcoming";
              const statusClassName = cn(
                "shrink-0 border-transparent px-2 py-0.5 text-[11px] font-semibold",
                isCompleted && "bg-emerald-100 text-emerald-800",
                isPastDeadline && "bg-[#fde8ed] text-crimson-700",
                isDueToday && "bg-amber-100 text-amber-800",
                !isCompleted && !isPastDeadline && !isDueToday && "bg-amber-100 text-amber-800"
              );
              const deadlineKey = deadline.deadline_id ?? `${deadlineDate.toISOString()}-${deadline.description}`;
              const viewUrl = deadline.deadline_id
                ? `/matter/${slug}/f/${documentGuid}?from=case-files/correspondence&taskId=${encodeURIComponent(deadline.deadline_id)}`
                : undefined;

              return (
                <div key={deadlineKey} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <DeadlineDate date={deadline.date} />
                      <Badge variant="outline" className={statusClassName}>
                        {statusLabel}
                      </Badge>
                    </div>
                    <div className="mt-1.5 text-sm leading-5 text-gray-700">
                      {deadline.description}
                    </div>
                  </div>
                  {viewUrl && (
                    <Button
                      href={viewUrl}
                      target="_blank"
                      variant="outline"
                      size="xs"
                      icon={ExternalLink}
                      text="View"
                      className="h-7 border-gray-200 bg-white px-2 text-gray-700 shadow-xs hover:bg-gray-50 hover:text-gray-900"
                      onClick={(event) => event.stopPropagation()}
                    />
                  )}
                </div>
              );
            })}
          </div>
      </>
    </HoverPopover>
  );
};
